import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const hexToRgb = (hex, fallback) => {
    if (!hex || typeof hex !== 'string') return fallback;
    let normalized = hex.trim().replace('#', '');
    if (normalized.length === 3) {
        normalized = normalized.split('').map((char) => char + char).join('');
    }
    if (!/^[a-fA-F0-9]{6}$/.test(normalized)) return fallback;

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
};

// Dark mode always renders with this fixed, stable green instead of the
// company's saved Business Customization color — some brand colors (light
// pastels, low-contrast hues, etc.) don't read well against dark surfaces.
// Light mode is unaffected: it always uses the real saved brand color.
// `secondary` is a 25%-darker shade of `primary`, for the
// `linear-gradient(135deg, primary, secondary)` pattern used throughout
// the app.
const DARK_MODE_BRAND_COLORS = {
    primary: '#039438',
    secondary: '#026f2a',
};

export const ThemeProvider = ({ children }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('isDarkMode');
        return savedTheme ? JSON.parse(savedTheme) : true;
    });

    // The company's actual saved Business Customization colors — always
    // read/written as-is (and persisted as-is), independent of the current
    // theme. This is what the color pickers in Business Customization edit.
    const [savedBrandColors, setSavedBrandColors] = useState(() => {
        const savedColors = localStorage.getItem('brandColors');
        return savedColors ? JSON.parse(savedColors) : { primary: '#3b82f6', secondary: '#1e293b' };
    });

    // What components should actually render with — the saved brand color in
    // light mode, or the fixed dark-mode green above. Everything downstream
    // (inline styles reading `brandColors.primary`, and the CSS variables set
    // below that back Tailwind's `bg-primary`/`text-primary`/etc.) consumes
    // this, not `savedBrandColors`, so the override applies app-wide from one
    // place.
    const brandColors = isDarkMode ? DARK_MODE_BRAND_COLORS : savedBrandColors;

    useEffect(() => {
        localStorage.setItem('isDarkMode', JSON.stringify(isDarkMode));
        if (isDarkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [isDarkMode]);

    useEffect(() => {
        localStorage.setItem('brandColors', JSON.stringify(savedBrandColors));
    }, [savedBrandColors]);

    useEffect(() => {
        const root = document.documentElement;

        const safePrimary = brandColors?.primary || '#3b82f6';
        const safeSecondary = brandColors?.secondary || '#1e293b';

        root.style.setProperty('--primary-color', safePrimary);
        root.style.setProperty('--secondary-color', safeSecondary);

        const primaryRgb = hexToRgb(safePrimary, '59, 130, 246');
        const secondaryRgb = hexToRgb(safeSecondary, '30, 41, 59');
        root.style.setProperty('--primary-rgb', primaryRgb);
        root.style.setProperty('--secondary-rgb', secondaryRgb);

        // Space-separated channel variables, distinct from --primary-rgb /
        // --secondary-rgb above (which are comma-separated and already
        // consumed via the legacy `rgba(var(--x), alpha)` pattern in several
        // places). Tailwind's `rgb(var(--x) / <alpha-value>)` color syntax
        // (used in tailwind.config.js so bg-primary/10 etc. resolve to the
        // effective brand color) requires space-separated channels — mixing
        // comma and slash syntax in one rgb() is invalid CSS.
        root.style.setProperty('--primary-rgb-channels', primaryRgb.replace(/,\s*/g, ' '));
        root.style.setProperty('--secondary-rgb-channels', secondaryRgb.replace(/,\s*/g, ' '));

        // Premium system background tokens (light mode defaults — overridden by html.dark CSS for dark mode)
        root.style.setProperty('--surface-base', '#f6faf8');
        root.style.setProperty('--surface-elevated', '#ffffff');
        root.style.setProperty('--surface-muted', '#eef5f2');
        root.style.setProperty('--surface-tint-primary', `rgba(${primaryRgb}, 0.09)`);
        root.style.setProperty('--surface-tint-secondary', `rgba(${secondaryRgb}, 0.08)`);
        root.style.setProperty('--surface-border-soft', `rgba(${secondaryRgb}, 0.12)`);
    }, [brandColors]);

    const toggleTheme = useCallback(() => setIsDarkMode(prev => !prev), []);
    const updateBrandColors = useCallback((newColors) => {
        setSavedBrandColors(prev => ({ ...prev, ...newColors }));
    }, []);

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleTheme, brandColors, updateBrandColors, savedBrandColors }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
